// Frontend request-timing telemetry.
//
// Every API call through apiClient is measured in the browser (the number the
// user actually feels) and combined with the backend's Server-Timing response
// header (app total + db time), so each slow request decomposes into:
//
//   total (browser)  =  net_ms (network/proxy/queue)  +  server_app_ms
//   server_app_ms    =  python/event-loop  +  server_db_ms
//
// Entries are buffered and shipped in batches to POST /diag/fe-timings, where
// the backend writes fe_timings.jsonl next to its own req_timings.jsonl; the
// two sides join on the X-Request-ID the client generates per request.
// Analysis: dezshop-operations/tasks/perf/analyze_req_timings.py.
//
// Volume control: we keep every slow (>= SLOW_KEEP_MS) or failed request and
// a small random sample of fast ones, so the beacon traffic stays negligible.

import authService from './auth.service';
import { createApiUrl } from './api.utils';

const SLOW_KEEP_MS = 1500;   // always keep entries at least this slow
const WARN_MS = 3000;        // also console.warn, with the breakdown
const FAST_SAMPLE = 0.03;    // sample 3% of fast requests for baselines
const FLUSH_AT = 15;         // batch size
const FLUSH_INTERVAL_MS = 45000;
const MAX_BUFFER = 200;      // survive a backend outage without growing memory
const MAX_BATCH = 50;        // below the server's 100-entry and keepalive limits

const BEACON_PATH = '/diag/fe-timings';

let buffer = [];
let flushTimer = null;
let listenersInstalled = false;
let flushing = false;
let retryAt = 0;

function parseServerTiming(response) {
  // "app;dur=123, db;dur=45.6;desc=\"12q\""
  try {
    const header = response && response.headers && response.headers.get('server-timing');
    if (!header) return { app: null, db: null };
    const app = /(?:^|,)\s*app;dur=([\d.]+)/.exec(header);
    const db = /(?:^|,)\s*db;dur=([\d.]+)/.exec(header);
    return { app: app ? parseFloat(app[1]) : null, db: db ? parseFloat(db[1]) : null };
  } catch {
    return { app: null, db: null };
  }
}

function pathOf(url) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url).slice(0, 200);
  }
}

async function flush(useKeepalive = false) {
  if (flushing || !buffer.length || Date.now() < retryAt) return;
  flushing = true;
  let entries = [];
  try {
    while (buffer.length) {
      const token = authService.isAuthenticated() ? authService.getAccessToken() : null;
      if (!token) {
        buffer = [];
        return;
      }
      entries = buffer.splice(0, MAX_BATCH);
      const response = await fetch(createApiUrl(BEACON_PATH), {
        method: 'POST',
        keepalive: useKeepalive,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ entries }),
      });
      // fetch resolves on HTTP errors too. Keep temporary failures for the
      // next interval instead of silently discarding the slow-request evidence.
      if (response.status === 401 || response.status === 408 || response.status === 429 || response.status >= 500) {
        throw new Error('Telemetry temporarily unavailable');
      }
      entries = [];
      if (!response.ok) break; // do not retry a permanently rejected payload
    }
  } catch {
    // Backend unreachable — exactly the moment whose evidence we must NOT
    // lose. Re-queue (newest dropped beyond MAX_BUFFER) and retry on the
    // next interval, once connectivity returns.
    buffer = entries.concat(buffer).slice(0, MAX_BUFFER);
    retryAt = Date.now() + FLUSH_INTERVAL_MS;
  } finally {
    flushing = false;
  }
}

function scheduleFlush() {
  if (!flushTimer) {
    flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  }
  if (!listenersInstalled && typeof document !== 'undefined') {
    listenersInstalled = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush(true);
    });
  }
}

/**
 * Record one measured API request.
 * @param {string} url full request URL
 * @param {string} method HTTP method
 * @param {string} rid X-Request-ID sent with the request
 * @param {number} t0 performance.now() at request start
 * @param {Response|null} response fetch response (null on network error)
 * @param {string|null} err short error tag ('timeout', 'network', ...)
 */
export function recordApiTiming(url, method, rid, t0, response, err) {
  try {
    const path = pathOf(url);
    if (path.includes('/diag/fe-timings')) return; // never instrument the beacon itself

    const totalMs = Math.round(performance.now() - t0);
    const keep = totalMs >= SLOW_KEEP_MS || !!err || Math.random() < FAST_SAMPLE;
    if (!keep) return;

    const { app, db } = parseServerTiming(response);
    const entry = {
      ts: Date.now() / 1000,
      rid,
      method: (method || 'GET').slice(0, 8),
      path: path.slice(0, 200),
      status: response ? response.status : 0,
      total_ms: totalMs,
      server_app_ms: app,
      server_db_ms: db,
      net_ms: app != null ? Math.max(0, Math.round(totalMs - app)) : null,
      err: err ? String(err).slice(0, 100) : null,
    };

    if (totalMs >= WARN_MS || err) {
      const netPart = entry.net_ms != null ? `${entry.net_ms}ms net + ` : '';
      console.warn(
        `[perf] ${entry.method} ${path} → ${totalMs}ms (${netPart}${app ?? '?'}ms server, din care ${db ?? '?'}ms DB)${err ? ` [${err}]` : ''} rid=${rid}`,
      );
    }

    if (buffer.length < MAX_BUFFER) buffer.push(entry);
    scheduleFlush();
    if (buffer.length >= FLUSH_AT) flush(false);
  } catch {
    // never throw from telemetry
  }
}

export function newRequestId() {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  } catch {
    return Math.random().toString(36).slice(2, 18);
  }
}
